/**
 * AdminSiteLocaleController — 언어 설정 페이지 JS 초기화.
 *
 * edit():
 *   1. initLocaleSelects() — 기본 언어 select (data-locale-select) SlimSelect 적용
 *   2. initLocaleAddSelect() — 언어 추가 selectbox (data-locale-add-select) 처리
 *      선택 시 toggle 라우트 POST → 성공하면 페이지 리로드
 *   3. initLocaleSortable() — 언어 카드 drag-drop 순서 변경
 *      드래그 종료 시 PATCH reorder 엔드포인트로 code 배열 전송
 */

import Sortable from "sortablejs";
import { initLocaleSelects } from "../../components/LocaleSelectWrapper";
import { SelectWrapper } from "../../components/SelectWrapper";
import { Toast } from "../../utils/Toast";
import { ToggleSwitchHandler } from "../components/ToggleSwitchHandler";

function initLocaleAddSelect(): void {
    const sel = document.querySelector<HTMLSelectElement>(
        "[data-locale-add-select]:not([data-locale-add-inited])"
    );
    if (!sel) return;

    sel.dataset.localeAddInited = "1";

    new SelectWrapper(sel, {
        placeholder: "언어를 선택해 추가…",
        searchPlaceholder: "언어 검색…",
        emptyMessage: "추가 가능한 언어가 없습니다.",
    }).init();

    sel.addEventListener("change", async () => {
        const code = sel.value.trim();
        if (!code) return;

        const toggleUrl = sel.dataset.toggleUrl ?? "";
        if (!toggleUrl) return;

        const url = toggleUrl.replace("__CODE__", encodeURIComponent(code));

        const tokenInput = document.querySelector<HTMLInputElement>("input[name=_token]");
        const token = tokenInput?.value ?? "";

        const fd = new FormData();
        fd.append("_token", token);

        try {
            const res = await fetch(url, {
                method: "POST",
                headers: { Accept: "application/json" },
                body: fd,
                credentials: "same-origin",
            });

            const data = (await res.json()) as { result: string; msg?: string };

            if (data.result === "success") {
                Toast.success(data.msg ?? "언어를 추가했습니다.");
                window.location.reload();
            } else {
                Toast.error(data.msg ?? "언어 추가에 실패했습니다.");
                sel.value = "";
                sel.dispatchEvent(new Event("change", { bubbles: true }));
            }
        } catch {
            Toast.error("네트워크 오류가 발생했습니다.");
            sel.value = "";
            sel.dispatchEvent(new Event("change", { bubbles: true }));
        }
    });
}

function initLocaleSortable(): void {
    const list = document.querySelector<HTMLElement>("[data-locale-sort-list]");
    if (!list) return;

    const endpoint = list.dataset.sortUrl ?? "";
    if (!endpoint) return;

    const csrf =
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ??
        document.querySelector<HTMLInputElement>('input[name="_token"]')?.value ??
        "";

    Sortable.create(list, {
        animation: 150,
        handle: ".admin-form__locale-handle",
        ghostClass: "admin-sortable__ghost",
        dragClass: "admin-sortable__drag",
        draggable: "[data-locale-code]",
        onEnd: () => void saveLocaleOrder(list, endpoint, csrf),
    });
}

async function saveLocaleOrder(list: HTMLElement, endpoint: string, csrf: string): Promise<void> {
    const codes = Array.from(
        list.querySelectorAll<HTMLElement>("[data-locale-code]")
    ).map((el) => el.dataset.localeCode ?? "").filter(Boolean);

    try {
        const res = await fetch(endpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest",
                "X-CSRF-TOKEN": csrf,
            },
            body: JSON.stringify({ items: codes, _method: "PATCH" }),
            credentials: "same-origin",
        });

        const data = (await res.json()) as { result: string; msg?: string };

        if (data.result === "success") {
            Toast.success(data.msg ?? "순서가 저장되었습니다.");
        } else {
            Toast.error(data.msg ?? "순서 저장에 실패했습니다.");
        }
    } catch {
        Toast.error("순서 저장 중 오류가 발생했습니다.");
    }
}

export const AdminSiteLocaleController = {
    edit(): void {
        initLocaleSelects();
        initLocaleAddSelect();
        initLocaleSortable();
        ToggleSwitchHandler.init(document);
    },

    update(): void {
        initLocaleSelects();
    },
};
